import { z } from "zod"

export const SUBMISSION_STAGE_STATE = {
  pending: "pending",
  active: "active",
  waiting: "waiting",
  complete: "complete",
  blocked: "blocked",
  failed: "failed",
} as const

export const SUBMISSION_OBSERVATION_FRESHNESS = {
  current: "current",
  stale: "stale",
  unknown: "unknown",
} as const

export const CATALOG_PUBLICATION = {
  published: "published",
  notPublished: "not_published",
  reportedErrors: "reported_errors",
  unobserved: "unobserved",
  unavailable: "unavailable",
  unsupported: "unsupported",
} as const
export const catalogMessageSchema = z
  .object({ code: z.string(), message: z.string() })
  .passthrough()
export const catalogSnapshotSchema = z.object({
  observedAt: z.string(),
  freshUntil: z.string(),
  status: z.string(),
  errors: z.array(catalogMessageSchema),
  warnings: z.array(catalogMessageSchema),
  references: z.array(z.object({ code: z.string(), value: z.string() })),
})
export const catalogFailureSchema = z.object({
  observedAt: z.string(),
  reason: z.string(),
  errors: z.array(catalogMessageSchema),
})
export const catalogProductSchema = z.object({
  id: z.string(),
  sellerId: z.string(),
  reference: z.string(),
  nextReadAt: z.string().nullable(),
  lastAttemptAt: z.string().nullable(),
  stoppedAt: z.string().nullable(),
  stopReason: z.string().nullable(),
  snapshot: catalogSnapshotSchema.nullable(),
  lastReadFailure: catalogFailureSchema.nullable(),
  firstFailure: catalogFailureSchema.nullable(),
  latestFailure: catalogFailureSchema.nullable(),
})
export const catalogObservationSchema = z.object({
  version: z.literal(1),
  accountScope: z.string(),
  shopId: z.number().int().positive(),
  artifactSha256: z.string(),
  enrolledAt: z.string(),
  enrolledBy: z.string(),
  deadline: z.string(),
  products: z.array(catalogProductSchema),
})
export type CatalogObservation = z.infer<typeof catalogObservationSchema>
export type CatalogProduct = z.infer<typeof catalogProductSchema>
export const catalogProductProgressSchema = catalogProductSchema.extend({
  publication: z.enum(CATALOG_PUBLICATION),
  freshness: z.enum(SUBMISSION_OBSERVATION_FRESHNESS),
})
export const catalogSummarySchema = z.object({
  capability: z.enum(["supported", "unsupported"]),
  published: z.number(),
  notPublished: z.number(),
  reportedErrors: z.number(),
  unobserved: z.number(),
  unavailable: z.number(),
  stale: z.number(),
  stopped: z.number(),
  warnings: z.number(),
  pending: z.number(),
  images: z.literal("unsupported_unverified"),
  imagesUnverified: z.number().int().nonnegative(),
  revisionAcceptance: z.literal("unverified"),
  detail: z.string(),
})

export const submissionStageSchema = z.object({
  state: z.enum(SUBMISSION_STAGE_STATE),
  detail: z.string().nullable(),
})
export type SubmissionStage = z.infer<typeof submissionStageSchema>

const countSchema = z.number().int().nonnegative().nullable()

export const submissionBatchSchema = z.object({
  catalogUnavailableReason: z.string().nullable().optional(),
  id: z.string(),
  label: z.string(),
  status: z.string(),
  sent: countSchema,
  confirmed: countSchema,
  rejected: countSchema,
  unresolved: countSchema,
  submittedAt: z.string().nullable(),
  observedAt: z.string().nullable(),
  error: z.string().nullable(),
  catalog: catalogObservationSchema
    .omit({ products: true })
    .extend({ products: z.array(catalogProductProgressSchema) })
    .nullable()
    .optional(),
  products: z.array(
    z.object({ sku: z.string(), status: z.string(), message: z.string().nullable() })
  ),
})
export type SubmissionBatch = z.infer<typeof submissionBatchSchema>

/** Persisted evidence only. Reading progress never dispatches or repairs work.
 * Null counts mean unknown; accepted writes alone never complete verification. */
export const submissionProgressSchema = z.object({
  submissionId: z.string().nullable(),
  artifactSha256: z.string().optional(),
  status: z.string(),
  stages: z.object({
    prepare: submissionStageSchema,
    approve: submissionStageSchema,
    send: submissionStageSchema,
    verify: submissionStageSchema,
  }),
  counts: z.object({
    total: countSchema,
    sent: countSchema,
    confirmed: countSchema,
    rejected: countSchema,
    awaiting: countSchema,
    unresolved: countSchema,
  }),
  waitingReason: z.string().nullable(),
  nextAction: z.string().nullable(),
  nextActionAt: z.string().nullable(),
  observedAt: z.string().nullable(),
  observationFreshness: z.enum(SUBMISSION_OBSERVATION_FRESHNESS),
  historyAvailable: z.boolean(),
  catalog: catalogSummarySchema.optional(),
  completionSummary: z.string().optional(),
  batches: z.array(submissionBatchSchema),
  batchPage: z
    .object({
      offset: z.number().int().nonnegative(),
      nextOffset: z.number().int().nonnegative().nullable(),
    })
    .optional(),
})
export type SubmissionProgress = z.infer<typeof submissionProgressSchema>

export const SUBMISSION_CONTROL_ACTION = {
  pause: "pause",
  resume: "resume",
  reconcile: "reconcile",
  observeCatalog: "observe-catalog",
} as const

export const submissionControlSchema = z
  .object({
    action: z.enum(SUBMISSION_CONTROL_ACTION),
    submissionId: z.string().min(1).optional(),
    artifactSha256: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
    unitId: z.number().int().positive().optional(),
    receiptId: z.string().min(1).optional(),
  })
  .strict()
  .refine(
    (control) =>
      control.action === SUBMISSION_CONTROL_ACTION.reconcile ||
      (control.unitId === undefined && control.receiptId === undefined),
    { message: "unitId and receiptId are only supported for reconciliation" }
  )
  .refine(
    (control) =>
      control.action === SUBMISSION_CONTROL_ACTION.observeCatalog
        ? Boolean(control.submissionId && control.artifactSha256)
        : control.submissionId === undefined && control.artifactSha256 === undefined,
    {
      message:
        "observe-catalog requires exact submissionId and artifactSha256; other actions cannot supply them",
    }
  )
export type SubmissionControl = z.infer<typeof submissionControlSchema>

export const submissionHistoryOffsetSchema = z.coerce.number().int().min(0).max(1000000)
